import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DocumentRecord, DocumentVersion, QRRecord } from '../types';
import { mockDocuments, mockQRRecords } from '../data/mockData';

interface DocumentsState {
  documents:  DocumentRecord[];
  qrRecords:  QRRecord[];
  loading:    boolean;
  replaceAll:     (documents: DocumentRecord[], qrRecords: QRRecord[]) => void;
  addDocument:    (doc: DocumentRecord, qr?: QRRecord) => void;
  updateDocument: (id: string, patch: Partial<DocumentRecord>) => void;
  replaceDocumentFile: (id: string, patch: Partial<DocumentRecord>, version: DocumentVersion) => void;
  deleteDocument: (id: string) => void;
  archiveDocument: (id: string) => void;
  restoreDocument: (id: string) => void;
  getById:        (id: string) => DocumentRecord | undefined;
  getByDept:      (deptId: string) => DocumentRecord[];
  getQR:          (docId: string) => QRRecord | undefined;
  getQRByToken:   (token: string) => QRRecord | undefined;
  disableQR:      (qrId: string, reason?: string, userId?: string) => void;
  enableQR:       (qrId: string) => void;
  recordQRScan:   (qrId: string) => void;
}

export const useDocumentsStore = create<DocumentsState>()(
  persist(
    (set, get) => ({
      documents: mockDocuments,
      qrRecords: mockQRRecords.map(record => ({
        ...record,
        resolvedUrl: record.resolvedUrl.startsWith('/q/') ? record.resolvedUrl : `/q/${record.token}`,
      })),
      loading: false,

      replaceAll: (documents, qrRecords) => set({ documents, qrRecords }),

      addDocument: (doc, qr) => set(state => ({
        documents: [...state.documents, doc],
        qrRecords: qr ? [...state.qrRecords, qr] : state.qrRecords,
      })),

      updateDocument: (id, patch) => set(state => ({
        documents: state.documents.map(document =>
          document.id === id
            ? { ...document, ...patch, updatedAt: new Date().toISOString() }
            : document,
        ),
      })),

      replaceDocumentFile: (id, patch, version) => set(state => ({
        documents: state.documents.map(document => {
          if (document.id !== id) return document;
          const versions = document.versions.map(item => ({ ...item, isActive: false }));
          return {
            ...document,
            ...patch,
            versions: [...versions, version],
            updatedAt: new Date().toISOString(),
          };
        }),
      })),

      deleteDocument: id => set(state => ({
        documents: state.documents.map(document =>
          document.id === id
            ? { ...document, status: 'deleted', updatedAt: new Date().toISOString() }
            : document,
        ),
      })),

      archiveDocument: id => set(state => ({
        documents: state.documents.map(document =>
          document.id === id
            ? { ...document, status: 'archived', archivedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
            : document,
        ),
      })),

      restoreDocument: id => set(state => ({
        documents: state.documents.map(document =>
          document.id === id
            ? { ...document, status: 'active', archivedAt: undefined, updatedAt: new Date().toISOString() }
            : document,
        ),
      })),

      getById: id => get().documents.find(document => document.id === id),
      getByDept: deptId => get().documents.filter(document => document.departmentId === deptId && document.status !== 'deleted'),
      getQR: docId => get().qrRecords.find(qr => qr.documentId === docId),
      getQRByToken: token => get().qrRecords.find(qr => qr.token === token),

      disableQR: (qrId, reason, userId) => set(state => ({
        qrRecords: state.qrRecords.map(qr => qr.id === qrId ? {
          ...qr,
          status: 'disabled',
          disabledAt: new Date().toISOString(),
          disabledBy: userId,
          disableReason: reason,
          updatedAt: new Date().toISOString(),
        } : qr),
      })),

      enableQR: qrId => set(state => ({
        qrRecords: state.qrRecords.map(qr => qr.id === qrId ? {
          ...qr,
          status: 'active',
          disabledAt: undefined,
          disabledBy: undefined,
          disableReason: undefined,
          updatedAt: new Date().toISOString(),
        } : qr),
      })),

      recordQRScan: qrId => set(state => ({
        qrRecords: state.qrRecords.map(qr => qr.id === qrId ? {
          ...qr,
          scanCount: qr.scanCount + 1,
          lastScannedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } : qr),
      })),
    }),
    { name: 'masar-documents' },
  ),
);
